import * as vega from 'vega-lib';
import interpolateColors from 'utils/interpolate-colors';

const schemeMongoDbGreen = interpolateColors([
  { r: 218, g: 255, b: 234 },
  { r: 115, g: 224, b: 161 },
  { r: 22, g: 204, b: 98 },
  { r: 0, g: 92, b: 38 },
]);

const schemeMongoDbBlue = interpolateColors([
  { r: 224, g: 236, b: 255 },
  { r: 168, g: 204, b: 255 },
  { r: 25, g: 110, b: 230 },
  { r: 10, g: 84, b: 189 },
  { r: 0, g: 47, b: 113 },
]);

const schemeMongoDbRed = interpolateColors([
  { r: 255, g: 228, b: 228 },
  { r: 251, g: 163, b: 163 },
  { r: 230, g: 25, b: 25 },
  { r: 191, g: 13, b: 13 },
  { r: 95, g: 2, b: 2 },
]);

const schemeRainbow = interpolateColors([
  { r: 0, g: 0, b: 255 },
  { r: 0, g: 255, b: 255 },
  { r: 0, g: 255, b: 0 },
  { r: 255, g: 255, b: 0 },
  { r: 255, g: 0, b: 0 },
]);

/**
 * Vega schemes MUST be lowercase, as otherwise they cannot be accessed during encoding.
 * This is because as part of the encode step they make all schemes lowercase, which means
 * that we can access it in the color swatches (as we access the vega scheme object directly),
 * but the charts themselves will not be able to.
 **/
vega.scheme('mdbgreen', schemeMongoDbGreen);
vega.scheme('mdbblue', schemeMongoDbBlue);
vega.scheme('mdbred', schemeMongoDbRed);
vega.scheme('rainbow', schemeRainbow);

// Discrete Colors
const CHART_COLORS = [
  '#16CC62',
  '#196EE6',
  '#E6B219',
  '#E6196E',
  '#19C3E6',
  '#116149',
  '#E65D19',
  '#ABABAB',
  '#1392AB',
  '#80340E',
  '#73E0A1',
  '#8CB6F2',
  '#F0D175',
  '#F075A8',
  '#75DBF0',
  '#70A092',
  '#F09E75',
  '#CDCDCD',
  '#71BECD',
  '#B3856E',
];

// Diverging Color Schemes
const CHART_COLORS_DIVERGING = [
  'blueorange',
  'redblue',
  'pinkyellowgreen',
  'redyellowgreen',
  'brownbluegreen',
  'rainbow',
];

// Sequential Color Schemes
const CHART_COLORS_SEQUENTIAL = ['mdbgreen', 'mdbblue', 'mdbred', 'greys'];

// Sequential Multihue Color Schemes
const CHART_COLORS_MULTIHUE = [
  'yellowgreen',
  'purpleblue',
  'yelloworangered',
  'yellowgreenblue',
  'viridis',
  'inferno',
  'magma',
  'plasma',
];

// onHover names for color schemes
const CONTINUOUS_COLOR_TITLE = Object.freeze({
  mdbgreen: 'Green',
  mdbblue: 'Blue',
  mdbred: 'Red',
  greys: 'Grey',
  yellowgreen: 'YellowGreen',
  purpleblue: 'PurpleBlue',
  yelloworangered: 'YellowOrangeRed',
  yellowgreenblue: 'YellowGreenBlue',
  viridis: 'Viridis',
  inferno: 'Inferno',
  magma: 'Magma',
  plasma: 'Plasma',
  blueorange: 'BlueOrange',
  redblue: 'RedBlue',
  pinkyellowgreen: 'PinkYellowGreen',
  redyellowgreen: 'RedYellowGreen',
  brownbluegreen: 'BrownBlueGreen',
  rainbow: 'Rainbow',
});

/**
 * The maximum size of chart axis labels.
 * @type {number}
 */
const AXIS_LABEL_MAX_PIXELS = 150;

/**
 * The size of the chart axis title, margins and padding,
 * to avoid scrollbars appearing.
 * @type {number}
 */
const AXIS_TITLE_BUFFER_PIXELS = 50;

/**
 * The smallest chart height in pixels, to avoid negative size errors.
 */
const MIN_CHART_HEIGHT = 50;

/**
 * The smallest chart width in pixels, to avoid negative size errors.
 */
const MIN_CHART_WIDTH = 50;

const VEGA_SPEC_AXIS_SETTINGS = {
  zindex: 0,
  labelOverlap: 'greedy',
  grid: false,
  encode: {
    grid: {
      update: {
        strokeOpacity: { value: 1 },
      },
    },
    title: {
      update: {
        font: { value: 'Akzidenz' },
        fontSize: { value: 16 },
      },
    },
    labels: {
      update: {
        font: { value: 'Akzidenz' },
        fontSize: { value: 12 },
        labelLimit: AXIS_LABEL_MAX_PIXELS,
      },
    },
  },
};

const VEGA_SPEC_LEGEND_SETTINGS = {
  encode: {
    symbols: {
      update: {
        fillOpacity: { value: 1 },
        strokeWidth: { value: 3 },
        shape: { value: 'circle' },
      },
    },
    title: {
      update: {
        fontSize: { value: 12 },
        font: { value: 'Akzidenz' },
      },
    },
    labels: {
      update: {
        fontSize: { value: 11 },
        font: { value: 'Akzidenz' },
      },
    },
  },
};

export {
  CHART_COLORS,
  CHART_COLORS_DIVERGING,
  CHART_COLORS_SEQUENTIAL,
  CHART_COLORS_MULTIHUE,
  CONTINUOUS_COLOR_TITLE,
  AXIS_LABEL_MAX_PIXELS,
  AXIS_TITLE_BUFFER_PIXELS,
  MIN_CHART_HEIGHT,
  MIN_CHART_WIDTH,
  VEGA_SPEC_LEGEND_SETTINGS,
  VEGA_SPEC_AXIS_SETTINGS,
};
